import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Alert } from '@/components/UI/Alert';
import { Spinner } from '@/components/UI/Spinner';
import { apiErrorMessage } from '@/utils/apiError';
import {
  useListToolErrorTypesQuery,
  useListToolErrorsQuery,
  type ToolError,
} from '@/redux/internal-services/toolErrorsApi';
import { ToolErrorEditDialog } from './ToolErrorEditDialog';

/**
 * Every catalogued reply one SAM tool can return — its success message(s) and its errors — with the
 * same edit dialog as the Tool Errors page. Edits change the shared entry, so every agent that uses
 * the tool sees them.
 */
export const ToolErrorsForTool: React.FC<{ toolName: string }> = ({ toolName }) => {
  const { t } = useTranslation();
  const responsesQuery = useListToolErrorsQuery({ tool: toolName }, { skip: !toolName });
  const typesQuery = useListToolErrorTypesQuery();
  const types = typesQuery.data?.data.types;
  const typeDefault = useMemo(
    () => Object.fromEntries((types ?? []).map((x) => [x.type, x.retryable])),
    [types],
  );
  const successTypes = useMemo(
    () => new Set((types ?? []).filter((x) => x.outcome === 'success').map((x) => x.type)),
    [types],
  );
  const [editing, setEditing] = useState<ToolError | null>(null);
  const responses = responsesQuery.data?.data ?? [];
  const isSuccess = (e: ToolError) => !!e.default_for || successTypes.has(e.type);
  const groups = [
    { key: 'success', label: t('platform.toolErrors.onSuccess'), items: responses.filter(isSuccess) },
    { key: 'failure', label: t('platform.toolErrors.onFailure'), items: responses.filter((e) => !isSuccess(e)) },
  ].filter((g) => g.items.length > 0);
  const yesNo = (v: boolean | undefined) => (v ? t('platform.toolErrors.yes') : t('platform.toolErrors.no'));

  return (
    <div className="space-y-2 border-t border-ui-border pt-3">
      <div>
        <p className="text-sm font-medium text-ui-text">{t('platform.toolErrors.agentSectionTitle')}</p>
        <p className="text-xs text-ui-text3">{t('platform.toolErrors.agentSectionHint')}</p>
      </div>

      {responsesQuery.isLoading || typesQuery.isLoading ? (
        <Spinner size="sm" />
      ) : responsesQuery.isError || typesQuery.isError ? (
        <Alert tone="danger">
          {apiErrorMessage(responsesQuery.error ?? typesQuery.error, t, t('platform.toolErrors.loadFailed'))}
        </Alert>
      ) : groups.length === 0 ? (
        <p className="text-xs text-ui-text3">{t('platform.toolErrors.agentSectionEmpty')}</p>
      ) : (
        groups.map((g) => (
          <div key={g.key} className="space-y-1">
            <p className="text-caption font-medium text-ui-text2">{g.label}</p>
            <ul className="divide-y divide-ui-border rounded-control border border-ui-border">
              {g.items.map((e) => {
                const success = g.key === 'success';
                return (
                  <li key={e.code} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono text-xs text-ui-text">{e.name}</span>
                        <span className="font-mono text-caption text-ui-text3" title={t('platform.toolErrors.code')}>{e.code}</span>
                        <Badge variant={success ? 'success' : 'accent'} className="font-mono">{e.type}</Badge>
                        {!success && e.type_from_exception && (
                          <span className="text-caption text-ui-text3">{t('platform.toolErrors.typeFromException')}</span>
                        )}
                        {!success && (
                          <span className="text-caption text-ui-text3">
                            {t('platform.toolErrors.retryable')}:{' '}
                            {e.retryable === null ? t('platform.toolErrors.inherits', { value: yesNo(typeDefault[e.type]) }) : yesNo(e.retryable)}
                          </span>
                        )}
                        {!e.is_active && <Badge variant="neutral">{t('platform.toolErrors.inactive')}</Badge>}
                      </div>
                      <p className="mt-0.5 break-words font-mono text-xs text-ui-text2">{e.message}</p>
                    </div>
                    <Button type="button" variant="link" size="xs" onClick={() => setEditing(e)}>{t('platform.toolErrors.edit')}</Button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))
      )}

      {editing && (
        <ToolErrorEditDialog
          error={editing}
          typeDefault={typeDefault}
          isSuccess={isSuccess(editing)}
          onClose={() => setEditing(null)}
          note={t('platform.toolErrors.sharedNote', { count: (editing.tools ?? []).length })}
        />
      )}
    </div>
  );
};

export default ToolErrorsForTool;
